<template>
  <div class="ceremony" :class="{'show-bottom-border': !hide_bottom_border}">

    <!-- ceremony header -->
    <div class="ceremony-header" :class="{ disabled: disabled }">
      <a href="/checklist" @click.prevent="navigateToChecklist(ceremony_info.ceremony)">
        {{getCeremonyHeader(ceremony_info)}}<span v-if="showNeedsReviewIndicator(ceremony_info.ceremony)" class="ceremony-needs-review-dot">{{'\u{2022}'}}</span>
      </a>
      <div ref="percentageStatRef" :class="{ 'percentage-stat': true, 'no-films-viewed': (ceremonyFilmsViewed === 0) }">{{this.percentageViewed}}% viewed</div>
    </div>

    <!-- ceremony needs review message -->
    <div v-if="showNeedsReviewMessage" class="review-ceremony-message">
      <a href="/checklist" @click.prevent="navigateToChecklist(ceremony_info.ceremony)">
        Start Tracking Progress by Reviewing <br>the {{getCeremonyOrdinalText(ceremony_info.ceremony)}} Ceremony Checklist
      </a>
    </div>

    <!-- top category row : always displayed -->
    <CeremonyCardTopRow
        :ceremony="ceremony_info.ceremony"
        :categories="rowCategories[0]"
        :disabled="disabled"
    >
    </CeremonyCardTopRow>

    <!-- more category rows : display if we're supposed to -->
    <div v-if="numRowsToDisplay > 1">
      <CeremonyCardRow
          v-for="row in numRowsToDisplay"
          :key="row"
          :ceremony="ceremony_info.ceremony"
          :categories="rowCategories[row]"
          :disabled="disabled"
      >
      </CeremonyCardRow>
    </div>

    <div class="ceremony-footer">
      <div v-if="!only_show_expanded" class="btn btn-sm btn-show-more" :class="{inactive: showNeedsReviewMessage}" type="button" @click="showExpanded = !showExpanded">
        <i :class="showExpanded? 'fa fa-caret-up' : 'fa fa-caret-down'" aria-hidden="true"></i>
        <span v-text="showExpanded? ' hide all but top row ' : ' show all categories '" aria-hidden="true"></span>
      </div>
    </div>

  </div>
</template>


<script>
import {Film} from "@/film";
import {userStore} from "@/userStore";
import CeremonyCardTopRow from "@/components/dashboard/CeremonyCardTopRow.vue";
import CeremonyCardRow from "@/components/dashboard/CeremonyCardRow.vue";
import {nextTick} from "vue";
import {EventBus} from "@/eventBus";

// date format object for ceremony date at top
const dateFormatObj = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

export default {
  name: "CeremonyCard",
  components: {CeremonyCardTopRow, CeremonyCardRow},
  props: ['ceremony_info', 'hide_needs_review_indicator', 'hide_bottom_border', 'only_show_expanded', 'disabled'],
  data() {
    return {
      CEREMONY_REVIEW_MESSAGE_ENABLED: false,
      rowCategories: [],
      showExpanded: !!this.only_show_expanded,
    }
  },
  computed : {
    numRowsToDisplay() {
      return ( this.showExpanded? this.rowCategories.length : Math.min(1, this.rowCategories.length) );
    },
    ceremonyReviewed() {
      return userStore.isCeremonyReviewed(this.ceremony_info.ceremony);
    },
    showNeedsReviewMessage() {
      return this.CEREMONY_REVIEW_MESSAGE_ENABLED && !this.ceremonyReviewed;
    },

    ceremonyFilmsViewed() {
      return this.ceremony_info.film_ids_contributing_to_percentage.reduce((accNumViewed, curFilmId) => {
        return accNumViewed + (userStore.isFilmMarkedViewed(curFilmId) ? 1 : 0);
      }, 0);
    },
    percentageViewed() {
      return ((this.ceremonyFilmsViewed / this.ceremony_info.film_ids_contributing_to_percentage.length) * 100).toFixed();
    },
  },
  created() {
    const categoryGroups = [
      // top group will always occupy the first row, and be these six categories
      ["Actor in a Leading Role", "Actor in a Supporting Role", "Best Picture", "Directing", "Actress in a Leading Role", "Actress in a Supporting Role"],

      // remaining categories will be on their own, with some pairs kept together
      ["Casting"],
      ["Documentary Feature Film"],
      ["Animated Feature Film"],
      ["International Feature Film"],
      ["Cinematography"],
      ["Writing (Original Screenplay)", "Writing (Adapted Screenplay)"],
      ["Music (Original Score)", "Music (Original Song)"],
      ["Visual Effects"],
      ["Production Design"],
      ["Film Editing"],
      ["Sound"],
      ["Makeup and Hairstyling", "Costume Design"],
      ["Live Action Short Film", "Documentary Short Film", "Animated Short Film"],
      ["Honorary Award"],
    ];

    const MAX_CATEGORIES_PER_ROW = 7;
    this.rowCategories = [];
    categoryGroups.forEach((categoryGroup, index) => {

      const nominationCategories = this.getNominationCategoriesForGroup(categoryGroup);

      // first row will always contain the first category group, and nothing else
      if (index === 0) {
        this.rowCategories.push(nominationCategories);
        this.rowCategories.push([]); // start next row

        // subsequent rows will squeeze as many category groups as well fit without exceeding the max
      } else {
        const rowIndex = this.rowCategories.length - 1;

        // if this is the first group added to a new row, just add it
        if (this.rowCategories[rowIndex].length === 0) {
          this.rowCategories[rowIndex] = this.rowCategories[rowIndex].concat(nominationCategories);

        // subsequent groups will be added to this row if they'll fit
        } else if ((this.rowCategories[rowIndex].length + nominationCategories.length) <= MAX_CATEGORIES_PER_ROW) {
          this.rowCategories[rowIndex] = this.rowCategories[rowIndex].concat(nominationCategories);

        // if it won't fit, start a new row
        } else {
          this.rowCategories.push(nominationCategories);
        }
      }
    });
  },
  mounted() {
    EventBus.on('viewed-state-updated', this.filmViewedStateUpdated);
    //console.log('mounted')
  },
  beforeUnmount() {
    EventBus.off('viewed-state-updated', this.filmViewedStateUpdated);
  },

  methods: {
    getCeremonyOrdinalText(_ceremony) {
      return Film.getCeremonyOrdinalText(_ceremony);
    },
    getCeremonyHeader(ceremony_info) {
      const ceremonyText = this.getCeremonyOrdinalText(ceremony_info.ceremony)
      const date = Film.getDateOfCeremony(ceremony_info.ceremony);
      return `${ceremonyText} Ceremony • ${dateFormatObj.format(date)}`;
    },
    showNeedsReviewIndicator(_ceremony) {
      if ( this.$screenshotMode || this.hide_needs_review_indicator ) {
        return false;
      }
      return !userStore.isCeremonyReviewed(_ceremony);
    },
    navigateToChecklist(_ceremony) {
      this.$router.push({
        name: 'ChecklistPage',
        params: {init_ceremony: _ceremony}
      });
    },

    getNominationCategoriesForGroup(_normalizedCategoryNames) {

      // filter the categories to just ones in the given argument
      let filteredCategories = this.ceremony_info.nomination_summary.filter((cat) => {
        return _normalizedCategoryNames.some((categoryInRow) => {
          return (categoryInRow === cat.category.normalized_name);
        });
      });

      // sort the categories so they're in the order represented by the row array above
      filteredCategories.sort((cat1, cat2) => {
        const index1 = _normalizedCategoryNames.findIndex((name) => {
          return (name === cat1.category.normalized_name);
        });
        const index2 = _normalizedCategoryNames.findIndex((name) => {
          return (name === cat2.category.normalized_name);
        });
        return index1 - index2;
      });

      return filteredCategories;
    },
    async filmViewedStateUpdated(_film_id) {
      //console.log(`filmViewedStateUpdated(${_film_id})`)
      if ( this.ceremony_info.film_ids_contributing_to_percentage.some(id => id === _film_id) && userStore.isFilmMarkedViewed(_film_id) ) {

        // We do the pulse on the next anim because it can be wiped out by some vue reactions
        //  (like removing the no-films-viewed class when the first box is checked).
        return nextTick().then(() => {
          if ( this.$refs.percentageStatRef ) {
            this.$refs.percentageStatRef?.classList.remove('percent-stat-pulse');
            void this.$refs.percentageStatRef.offsetWidth;
            this.$refs.percentageStatRef?.classList.add('percent-stat-pulse');
          }
        });
      }
    },
  },
}

</script>



<style scoped>
.ceremony {
  position: relative;

  .ceremony-header {
    position: relative;
    text-align: center;
    background-color: transparent;
    /*border: none;*/
    margin-bottom: 0.8em;

    a {
      font-size: 20px;
      font-weight: 500;

      color: var(--bs-secondary-color);
    }

    .ceremony-reviewed-achievement {
      margin-top: -6px;
      margin-right: 5px;
    }

    .ceremony-needs-review-dot {
      position: absolute;
      top: -0.375em;
      color: var(--mol-color-gold);
    }

    &.disabled {
      pointer-events: none;
      opacity: 20%;
    }
  }

  margin-top: 10px;
  margin-bottom: 25px;
  margin-right: auto;
  margin-left: auto;
  max-width: 450px;

  padding: 5px;

  &.show-bottom-border {
    border-style: none none solid none;
    border-color: lightgrey;
    border-width: 1px;
  }
  .ceremony-footer {
    background-color: transparent;
    border: none;
    text-align: center;
    min-height: 1.5em;
  }

  .btn-show-more {
    padding: 0;
    font-size: x-small;
    color: var(--bs-secondary);
    vertical-align: top;

    &.inactive {
      opacity: 20%;
      pointer-events: none;
    }
  }
}

.review-ceremony-message {

  text-align: center;
  font-weight: 500;
  font-size: small;
  z-index: 1;
  position: relative;

  a {
    position: absolute;
    top: 1.5em;
    left: 0;
    width: 100%;
    /*color: var(--bs-secondary); */
    color: var(--mol-color-gold)
  }

  a:hover {
    color: var(--bs-link-hover-color);
  }
}

a {
  text-decoration: inherit;
  color: var(--bs-secondary);
  cursor: pointer;
}
a:hover {
  color: var(--bs-link-hover-color);
}

.percentage-stat {
  font-family: 'Playpen Sans', cursive, sans-serif;
  color: var(--mol-color-gold-text);

  &.no-films-viewed {
    color: var(--bs-tertiary-color);
  }

}

.percent-stat-pulse {
  animation: pulse-animation 0.4s ease-out;
}

@keyframes pulse-animation {
  0%   { transform: scale(1); filter: brightness(1); }
  30%  { transform: scale(1.28); filter: brightness(1.15); }
  60%  { transform: scale(0.97); filter: brightness(1.05); } /* subtle dip */
  100% { transform: scale(1); filter: brightness(1); }
}





</style>